import { ChevronRightIcon } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type PageBreadcrumbItem = {
  label: string;
  /** When set, the segment is a link. Omit for the current page. */
  href?: string;
};

type PageBreadcrumbsProps = {
  items: PageBreadcrumbItem[];
  className?: string;
};

/**
 * Ancestor segments for PageWrapper `breadcrumb` (same line as the page title).
 * Renders an `<ol>` only — `PageWrapper` owns the breadcrumb landmark when paired with title.
 * Prefer linked parents; the current page is usually the PageWrapper `title` after the separator.
 */
export function PageBreadcrumbs({ items, className }: PageBreadcrumbsProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <ol className={cn("flex min-w-0 items-center gap-1", className)}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const content: ReactNode = item.href ? (
          <Link
            className="text-muted-foreground hover:text-foreground truncate transition-colors"
            href={item.href as Route}
          >
            {item.label}
          </Link>
        ) : (
          <span
            aria-current={isLast ? "page" : undefined}
            className={cn(
              "truncate",
              isLast ? "text-foreground font-medium" : "text-muted-foreground",
            )}
          >
            {item.label}
          </span>
        );

        return (
          <li className="flex min-w-0 items-center gap-1" key={`${item.label}-${index}`}>
            {index > 0 ? (
              <ChevronRightIcon
                aria-hidden="true"
                className="text-muted-foreground size-3.5 shrink-0"
              />
            ) : null}
            <span className="block min-w-0 truncate">{content}</span>
          </li>
        );
      })}
    </ol>
  );
}
